import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListAllParkings } from 'src/features/payStations/api/useListAllParkings';
import { useListAllPayStationHeartBeats } from 'src/features/payStations/api/useListAllPayStationHeartbeats';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import payStationsMessages from 'src/features/payStations/utils/messages';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

import type { PayStationHeartBeat } from 'src/features/payStations/types';

const AllHeartBeatsTable = () => {
    const { formatMessage } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'timestamp', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { data: parkingsData } = useListAllParkings({});
    const { data: payStationsData } = useListPayStations({});

    const { data, isLoading, isError } = useListAllPayStationHeartBeats({
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });
    const columnHelper = createColumnHelper<PayStationHeartBeat>();

    const columns = [
        columnHelper.accessor('device.identifier', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableColumnFilter: true,
            id: 'device',
            meta: {
                title: formatMessage(payStationsMessages.device),
                options: payStationsData?.results.map((payStation) => ({
                    value: payStation.id,
                    label: payStation.identifier,
                })),
            },
        }),
        columnHelper.accessor('device.parking.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            id: 'device__parking',
            meta: {
                title: formatMessage(payStationsMessages.parking),
                options: parkingsData?.results.map((parking) => ({ value: parking.id, label: parking.name })),
            },
        }),
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableColumnFilter: true,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(payStationsMessages.date),
                inputType: INPUT_TYPE.DATE,
            },
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            pagination={pagination}
            sorting={sorting}
            filters={filters}
            search={search}
            semantics={{
                title: formatMessage(payStationsMessages.allHeartBeats),
                entryName: formatMessage(payStationsMessages.heartBeat),
                entryNames: formatMessage(payStationsMessages.heartBeats),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default AllHeartBeatsTable;
